import type { ComponentType, ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface EmptyStateProps {
  icon?: ComponentType<{ className?: string }>;
  title: ReactNode;
  description?: ReactNode;
  /** Buttons under the copy — usually the one action that fills the empty state. */
  action?: ReactNode;
  /** Dashed card around the state, for empty lists that sit inside a page. */
  bordered?: boolean;
  /** Tighter spacing for inline states (a list column, a card body). */
  compact?: boolean;
  className?: string;
}

/**
 * What a surface shows when there is nothing in it yet: a brand-tinted icon tile, one sentence that
 * says what would be here, and the action that puts it there. Say what to do, not only what is missing.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  bordered = false,
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center motion-safe:animate-fade-up',
        compact ? 'gap-1.5 px-4 py-8' : 'gap-2 px-6 py-14',
        bordered && 'rounded-xl border border-dashed border-border bg-muted/20',
        className,
      )}
    >
      {Icon && (
        <div
          className={cn(
            'mb-2 flex items-center justify-center bg-primary/10 text-primary ring-1 ring-primary/15 dark:bg-primary/20 dark:text-foreground',
            compact ? 'size-10 rounded-xl' : 'size-12 rounded-2xl',
          )}
        >
          <Icon className={compact ? 'size-5' : 'size-6'} />
        </div>
      )}
      <p className={cn('font-medium text-foreground', compact ? 'text-sm' : 'text-base')}>{title}</p>
      {description && <p className="max-w-sm text-sm text-pretty text-muted-foreground">{description}</p>}
      {action && <div className="mt-3 flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  );
}
